import React, {
  useState,
  createContext,
  useMemo,
  useContext,
  Dispatch,
  SetStateAction,
  useEffect,
  PropsWithChildren,
  ReactNode,
} from 'react';
import { ProSidebarProvider } from 'react-pro-sidebar';
import { useIsMobile, useStyledTheme } from '../../styling';

const defaultSidebar = {};
const defaultSidebarFooter = <></>;
const defaultSidebarCollapsed = false;

type SidebarContextType = {
  sidebar: Record<string, ReactNode>,
  sidebarFooter: ReactNode,
  setSidebar: Dispatch<SetStateAction<Record<string, ReactNode>>>,
  setSidebarFooter: Dispatch<SetStateAction<ReactNode>>,
  sidebarCollapsed: boolean,
  setSidebarCollapsed: Dispatch<SetStateAction<boolean>>,
  toggleSidebarCollapsed: () => void,
}

export const SidebarContext = createContext<SidebarContextType>({
  sidebar: defaultSidebar,
  sidebarFooter: defaultSidebarFooter,
  setSidebar: () => { },
  setSidebarFooter: () => { },
  sidebarCollapsed: defaultSidebarCollapsed,
  setSidebarCollapsed: () => { },
  toggleSidebarCollapsed: () => { },
});

export function useSidebar() {
  return useContext(SidebarContext);
}

export function useSidebarMenu(sidebarId: string, menu: ReactNode, orderBefore = false) {
  const { setSidebar } = useSidebar();

  useEffect(() => {
    if (orderBefore) {
      setSidebar((prev) => ({ [sidebarId]: menu, ...prev }));
    } else {
      setSidebar((prev) => ({ ...prev, [sidebarId]: menu }));
    }

    return () => setSidebar((prev) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [sidebarId]: remove, ...rest } = prev;
      return rest;
    });
  }, [menu]);
}

export function useSidebarFooter(footer: ReactNode) {
  const { setSidebarFooter } = useSidebar();
  setSidebarFooter(footer);
}

export function SidebarProvider({ children }: PropsWithChildren) {
  const isMobile = useIsMobile();

  const [sidebar, setSidebar] = useState<Record<string, ReactNode>>(defaultSidebar);
  const [sidebarFooter, setSidebarFooter] = useState<ReactNode>(defaultSidebarFooter);
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(isMobile);

  const toggleSidebarCollapsed = () => setSidebarCollapsed((prev) => !prev);

  const theme = useStyledTheme();

  const sidebarValue = useMemo(() => ({
    sidebar,
    sidebarFooter,
    setSidebar,
    setSidebarFooter,
    sidebarCollapsed,
    setSidebarCollapsed,
    toggleSidebarCollapsed,
  }), [sidebar, sidebarCollapsed, theme]);

  return (
    <SidebarContext.Provider value={sidebarValue}>
      <ProSidebarProvider>
        {children}
      </ProSidebarProvider>
    </SidebarContext.Provider>
  );
}
